/**
 * @module apollo client
 *
 * All GraphQL requests to the WorkTango API use this Apollo client.
 *
 * For more on Apollo, see:
 * - https://www.apollographql.com/docs/react/v2/api/apollo-client/
 * - https://www.apollographql.com/docs/react/v2/data/fragments/#fragments-on-unions-and-interfaces
 * - https://www.apollographql.com/docs/react/v2/caching/cache-configuration/
 */
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { persistCache } from 'apollo3-cache-persist';
import { appName, env } from '~/config/settings/app.settings';
import { publicGraphqlQueries } from '~/config/settings/worktango.settings';
import { config } from '@worktango/ai-assistant/react';
import { errors } from '~/tools/errors';
import * as log from '../log';
import * as tokens from '../tokens';

const localStorage = window.localStorage;
/* v8 ignore start */
// Use standard HTTP link in test environment to ensure MSW can intercept requests
const isTestEnvironment = typeof globalThis.vi !== 'undefined' || import.meta.env.TEST === 'true';
// Ensure MSW can intercept
const httpLink = isTestEnvironment
  ? // Apollo Client: Using createHttpLink for test environment to enable MSW interception
    createHttpLink({
      uri: config.getPulseUrl('/graphql'),
      // We might want to mock fetch for testing purposes.
      // fetch: globalThis.fetch,
    })
  : createUploadLink({
      uri: config.getPulseUrl('/graphql'),
    });
/* v8 ignore end */

const authLink = setContext(async (req, prevContext) => {
  const { headers } = prevContext;
  const { operationName } = req;

  // If there is no `req.operationName` the query cannot be executed.
  if (!operationName) {
    throw errors.createError({
      message: `missing or invalid operation name "${operationName}"`,
    });
  }

  // If this is a public query, no need to fetch the auth token (even if there
  // is one) and simply make the query with no authorization header.
  if (publicGraphqlQueries.includes(operationName)) {
    return headers;
  }

  const authToken = tokens.getAccessToken();

  // REFACTOR: Given the current module structure, this creates a circular
  // dependency (where an action imports request which imports actions).
  // Move store to a separate module so it can be imported independently.
  //
  // If there is no auth token found, it is either not yet set, or it is expired.
  // A missing token is effectively "expired".
  if (!authToken) {
    const tokenExpiredError = errors.createError({
      // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'null | un... Remove this comment to see the full error message
      name: errors.types.tokenExpired,
      message: `${errors.types.tokenExpired}: please login again`,
    });

    log.error('apollo token expired: ', tokenExpiredError);

    // Explicitly redirect to /logout endpoint with tokenExpired error.
    //
    // NOTE: This is not the ideal way to do this, but is a way to globally log
    // out from outside the regular Redux/Flux data flow (e.g., from a legacy
    // Apollo hook) until this module is deprecated and removed.
    //
    // NOTE: Only execute this hack if loading a page from the legacy part of the
    // app (contains `/admin` in the URI). In this context, we are outside the
    // new Redux actions and throwing an error alone will not be enough to
    // trigger the logout, we also need to get back into the Redux flow and
    // trigger it directly. We can do this by visiting the /logout endpoint.
    //
    // We don't want to do this if we *are* within the Redux actions because it
    // is much faster and handled in a central middleware when we can just throw
    // the error. In a case where we are using both Apollo + Redux, redirecting
    // will still work but cause a strage delay. This condition is an optimization
    // to avoid that delay for a better UX.
    if (window.location.href.includes('/admin')) {
      window.location.href = `/logout?reason=${errors.types.tokenExpired}`;
    }

    throw tokenExpiredError;
  }

  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${authToken}`,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((graphQLError) => {
      log.error(graphQLError.toString(), {
        // @ts-expect-error TS(2551): Property 'location' does not exist on type 'GraphQ... Remove this comment to see the full error message
        location: graphQLError.location,
        path: graphQLError.path,
      });
    });
  }

  if (networkError) {
    // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
    log.error(networkError.toString());
  }
});

const cache = new InMemoryCache();

const initCache = async () => {
  return persistCache({
    cache,
    storage: localStorage,
  });
};

const client = new ApolloClient({
  name: `${appName}-apollo`,
  version: '2.0', // can be used to indicate version of cache
  connectToDevTools: !isTestEnvironment && env !== 'production',
  link: errorLink.concat(authLink.concat(httpLink)),
  cache,
  // @ts-expect-error TS(2345): Argument of type '{ name: string; version: string;... Remove this comment to see the full error message
  fetchOptions: {
    mode: 'cors',
  },
});

export { client, cache, initCache };
